import qz from 'qz-tray';
import axios from 'axios';
import { notification } from 'antd';

/*
 * QZ Tray integration — silent label printing from the browser to a local printer.
 *
 * QZ Tray is a free desktop agent (system tray) that exposes the machine's OS printers
 * over a localhost WebSocket. The browser opens that socket and sends a base64 PDF straight
 * to a chosen printer — no "open PDF in a new tab and hit Ctrl+P" step.
 *
 * Design rules (see the QZ Tray rollout plan):
 *   - Printers are bound to the PHYSICAL machine. localStorage is the synchronous cache the
 *     print path reads; the durable copy lives server-side in qz_printer_stations, keyed by the
 *     machine's identity (MAC via the agent — see the per-station persistence section), NOT by
 *     user. Two slots:
 *        qz_printer_zebra  -> thermal/Zebra printer for FNSKU / product (2"x1") labels
 *        qz_printer_label  -> 4x6 / document printer for box / carrier / pallet labels
 *   - Every print path keeps its CURRENT behavior (open the PDF / download) as an automatic
 *     fallback: if the agent isn't running, no printer is chosen, or the print is blocked,
 *     we silently fall back so printing never breaks.
 *   - Path A (free / unsigned) ships first: the agent shows an "Allow / Remember" prompt once
 *     per agent session per station. Path B (paid cert -> true silent) is a drop-in toggle:
 *     set REACT_APP_QZ_CERT_URL + REACT_APP_QZ_SIGN_URL and configureSigning() wires it up.
 */

export const PRINTER_KEYS = { zebra: 'qz_printer_zebra', label: 'qz_printer_label' };

// Fired on the window after a printer slot changes so same-tab listeners (the settings card,
// the nudge banner) can re-read config — the native `storage` event only fires in OTHER tabs.
export const PRINTERS_CHANGED_EVENT = 'qz-printers-changed';

export const getSavedPrinter = (target) => {
  try {
    return localStorage.getItem(PRINTER_KEYS[target]) || '';
  } catch (e) {
    return '';
  }
};

export const savePrinter = (target, name) => {
  try {
    if (name) localStorage.setItem(PRINTER_KEYS[target], name);
    else localStorage.removeItem(PRINTER_KEYS[target]);
  } catch (e) {
    /* localStorage unavailable (private mode / quota) — ignore, printing still falls back */
  }
  try {
    window.dispatchEvent(new Event(PRINTERS_CHANGED_EVENT));
  } catch (e) {
    /* no-op */
  }
};

// "Configured" = both slots chosen. The nudge UX keys off this; it is deliberately NOT tied to a
// live socket, so the banner doesn't flicker whenever the agent happens to be off.
export const isPrinterConfigured = () =>
  Boolean(getSavedPrinter('zebra')) && Boolean(getSavedPrinter('label'));

/* ----------------------------- per-USER feature toggle ---------------------------------------- */
// QZ Tray printing is an explicit per-account OPT-IN (Account page toggle). The printers stay
// per-STATION; this flag is per-PERSON: durable copy in user_active_preferences
// (context 'qz-tray-feature'), mirrored into localStorage so the synchronous print path can check
// it without a network hop. Default is DISABLED (missing key = off): until a user turns it on,
// every print helper goes straight to its fallback (open/download the PDF, exactly the pre-QZ
// behavior) and the setup banner + silent connects stay away.
export const QZ_FEATURE_KEY = 'qz_feature_enabled';
const QZ_FEATURE_CONTEXT = 'qz-tray-feature';

export const isQzFeatureEnabled = () => {
  try {
    return localStorage.getItem(QZ_FEATURE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

// Local mirror write; fires PRINTERS_CHANGED_EVENT so the banner/settings card re-read state.
export const setQzFeatureEnabledLocal = (enabled) => {
  try {
    localStorage.setItem(QZ_FEATURE_KEY, enabled ? '1' : '0');
  } catch (e) {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event(PRINTERS_CHANGED_EVENT));
  } catch (e) {
    /* no-op */
  }
};

// Pull the user's toggle from the server into localStorage (login / Account mount).
// Resolves the effective enabled state; on any failure keeps the local value.
export const fetchQzFeaturePreference = async () => {
  const base = process.env.OPULENT_NODE_API_URL || '';
  if (!base) return isQzFeatureEnabled();
  try {
    const res = await axios.get(`${base}/users/active-preference`, {
      params: { context_key: QZ_FEATURE_CONTEXT }
    });
    const enabled = res && res.data && res.data.data && res.data.data.applied_filters
      ? res.data.data.applied_filters.enabled
      : undefined;
    if (typeof enabled === 'boolean') setQzFeatureEnabledLocal(enabled);
    return isQzFeatureEnabled();
  } catch (e) {
    return isQzFeatureEnabled();
  }
};

// Persist the toggle: local mirror immediately (printing honors it at once), server copy after.
// Resolves false when the server save failed (local still applied).
export const saveQzFeaturePreference = async (enabled) => {
  setQzFeatureEnabledLocal(enabled);
  const base = process.env.OPULENT_NODE_API_URL || '';
  if (!base) return false;
  try {
    await axios.post(`${base}/users/active-preference`, {
      context_key: QZ_FEATURE_CONTEXT,
      applied_filters: { enabled }
    });
    return true;
  } catch (e) {
    return false;
  }
};

/* ----------------------------- Path B: message signing (silent printing) ---------------------- */
// When the backend QZ endpoints are configured (a private key is present server-side), this wires
// QZ's certificate + signature promises so every request is cryptographically signed -> the agent
// prints SILENTLY (no Allow/Block prompt). Our certificate is issued by QZ Industries, whose CA
// every QZ Tray trusts natively — no per-station trust setup needed.
//
// Degrades safely: if the backend has no key, /qz/digital-certificate 404s -> the certificate promise
// rejects -> QZ treats the request as unsigned and shows the Allow/Remember prompt (free tier). The
// requests go through the app's axios instance, which carries the auth token, so the endpoints stay
// behind auth (only logged-in users can fetch a signature).
let signingConfigured = false;
export const configureSigning = ({ certUrl, signUrl, algorithm = 'SHA512' } = {}) => {
  if (signingConfigured || !certUrl || !signUrl) return;
  qz.security.setCertificatePromise((resolve, reject) =>
    axios.get(certUrl).then((r) => resolve(r.data)).catch(reject));
  qz.security.setSignatureAlgorithm(algorithm); // 2.1+ (2.0 used SHA1)
  qz.security.setSignaturePromise((toSign) => (resolve, reject) =>
    axios.get(signUrl, { params: { request: toSign } }).then((r) => resolve(r.data)).catch(reject));
  signingConfigured = true;
};

/* ----------------------------- connection ----------------------------------------------------- */
export const isConnected = () => {
  try {
    return qz.websocket.isActive();
  } catch (e) {
    return false;
  }
};

// Connect once; safe to call repeatedly (isActive guard avoids "connection already active").
// Low retries by default so a missing agent fails fast into the fallback instead of hanging.
export const connect = async ({ retries = 2, delay = 1 } = {}) => {
  if (isConnected()) return true;
  // Point signing at the backend QZ endpoints (override per-env if ever hosted elsewhere). Wiring is
  // harmless when the server has no key — the cert fetch 404s and QZ just falls back to prompting.
  const apiBase = process.env.OPULENT_NODE_API_URL || '';
  configureSigning({
    certUrl: process.env.REACT_APP_QZ_CERT_URL || (apiBase && `${apiBase}/qz/digital-certificate`),
    signUrl: process.env.REACT_APP_QZ_SIGN_URL || (apiBase && `${apiBase}/qz/sign`),
  });
  await qz.websocket.connect({ retries, delay });
  return true;
};

export const disconnect = async () => {
  try {
    if (isConnected()) await qz.websocket.disconnect();
  } catch (e) {
    /* no-op */
  }
};

// Reconnect WITHOUT any risk of a popup. The websocket dies with the tab (reload, sleep, QZ Tray
// restart), so status badges go "not connected" until something reconnects. With the signed cert
// live on the backend, connect() is silent — but if signing is NOT configured, connecting would
// pop QZ's Allow/Block dialog uninvited. So probe the cert endpoint first and only connect when
// it answers (404 = signing off -> stay out and let the user connect explicitly).
export const connectIfSilent = async () => {
  if (!isQzFeatureEnabled()) return false; // user turned QZ printing off — never auto-connect
  if (isConnected()) return true;
  const apiBase = process.env.OPULENT_NODE_API_URL || '';
  const certUrl = process.env.REACT_APP_QZ_CERT_URL || (apiBase && `${apiBase}/qz/digital-certificate`);
  if (!certUrl) return false;
  try {
    await axios.get(certUrl);
    await connect({ retries: 1, delay: 1 });
    return true;
  } catch (e) {
    return false; // no signing / agent not running — caller keeps the manual Connect flow
  }
};

// List installed printers (used by the settings dropdowns). Connects first; throws if the agent
// isn't running so the caller can show "QZ Tray not detected".
export const listPrinters = async () => {
  await connect({ retries: 3, delay: 1 });
  const found = await qz.printers.find();
  return (Array.isArray(found) ? found : [found]).filter(Boolean);
};

/* ----------------------------- per-station persistence (DB) ----------------------------------- */
// Printers belong to the physical machine, so the durable copy on the server is keyed by a STATION
// identity — the machine's MAC address as reported by the QZ Tray agent (browsers can't read MACs;
// the agent can) — never by user_id. localStorage stays the synchronous cache the print path reads;
// the DB row survives cache clears and is shared by every user/browser on the PC.

export const STATION_KEY_STORAGE = 'qz_station_key';

const apiBase = () => process.env.OPULENT_NODE_API_URL || '';

const readCachedStationKey = () => {
  try {
    return localStorage.getItem(STATION_KEY_STORAGE) || '';
  } catch (e) {
    return '';
  }
};

const cacheStationKey = (key) => {
  try {
    localStorage.setItem(STATION_KEY_STORAGE, key);
  } catch (e) {
    /* ignore */
  }
};

const normalizeMac = (mac) => String(mac || '').toLowerCase().replace(/[^a-f0-9]/g, '');

// Ask the agent for the machine identity (privileged call — goes through the signing promises,
// like printing). Returns { stationKey: 'mac-…', hostname } or null when the agent is unreachable.
const readMacIdentity = async () => {
  try {
    if (!isConnected()) {
      const ok = await connectIfSilent();
      if (!ok) return null;
    }
    const device = await qz.networking.device();
    const mac = normalizeMac(device && (device.mac || device.macAddress));
    if (!mac) return null;
    return { stationKey: `mac-${mac}`, hostname: (device && device.hostname) || '' };
  } catch (e) {
    return null;
  }
};

const randomId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch (e) {
    /* fall through */
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};

// Resolve this machine's station identity. Prefers the hardware MAC (stable across browsers, users
// and cache clears); falls back to a generated browser id cached in localStorage. A cached
// browser-* key upgrades to the MAC key the first time the agent becomes reachable, so stations
// converge on hardware identity.
export const getStationIdentity = async () => {
  const cached = readCachedStationKey();
  if (cached.startsWith('mac-')) return { stationKey: cached, hostname: '' };
  const macIdentity = await readMacIdentity();
  if (macIdentity) {
    cacheStationKey(macIdentity.stationKey);
    return macIdentity;
  }
  if (cached) return { stationKey: cached, hostname: '' };
  const generated = `browser-${randomId()}`;
  cacheStationKey(generated);
  return { stationKey: generated, hostname: '' };
};

// Push the station's printers to the server (called on Save; also used to seed pre-DB stations).
// Omitted slots default to the current localStorage values. Resolves false on any failure so the
// caller can say "saved on this computer only".
export const saveStationPrinters = async ({ zebra, label } = {}) => {
  const base = apiBase();
  if (!base) return false;
  try {
    const { stationKey, hostname } = await getStationIdentity();
    await axios.post(`${base}/qz/printer-station`, {
      station_key: stationKey,
      hostname: hostname || undefined,
      zebra_printer: zebra !== undefined ? zebra : getSavedPrinter('zebra'),
      label_printer: label !== undefined ? label : getSavedPrinter('label'),
    });
    return true;
  } catch (e) {
    return false;
  }
};

// Pull the station's printers from the server into localStorage (login / Account mount). The DB
// row wins when it has printers — writing through savePrinter fires PRINTERS_CHANGED_EVENT, so the
// banner and settings card react on their own. A station that has local printers but no server row
// seeds the row instead (silent migration). Never throws; resolves true when localStorage changed.
export const fetchStationPrinters = async () => {
  if (!isQzFeatureEnabled()) return false; // feature off for this user — leave localStorage alone
  const base = apiBase();
  if (!base) return false;
  try {
    const { stationKey } = await getStationIdentity();
    const res = await axios.get(`${base}/qz/printer-station`, { params: { station_key: stationKey } });
    const row = res && res.data && res.data.data;
    if (row && (row.zebra_printer || row.label_printer)) {
      savePrinter('zebra', row.zebra_printer || '');
      savePrinter('label', row.label_printer || '');
      return true;
    }
    if (!row && isPrinterConfigured()) {
      await saveStationPrinters({});
    }
    return false;
  } catch (e) {
    return false;
  }
};

/* ----------------------------- printing ------------------------------------------------------- */
// Sensible default media per slot. Callers can override size/density/orientation per label type.
const DEFAULT_SIZE = {
  zebra: { width: 2.25, height: 1.25 }, // FNSKU / product thermal label
  label: { width: 4, height: 6 }, // box / carrier / pallet 4x6
};

/**
 * Print a base64-encoded PDF to a target slot's printer.
 * On ANY failure (no agent, no printer chosen, blocked, print error) runs `opts.fallback`
 * (today's behavior — e.g. open the PDF in a new tab) and resolves false instead of throwing.
 *
 * @param {'zebra'|'label'} target
 * @param {string} pdfBase64  raw base64 (no data: prefix)
 * @param {object} [opts]  { printer, size, units, orientation, density, colorType,
 *                           scaleContent, rasterize, copies, fallback }
 * @returns {Promise<boolean>} true if it printed via QZ, false if it fell back
 */
// Surfaced when QZ is enabled but the label could not be silent-printed (no printer chosen, agent
// unreachable, print failed). We deliberately do NOT fall back to opening the PDF in that case:
// the user opted in to silent printing, so silently handing them a PDF would hide that it's
// broken. Keyed so repeated failed prints update one toast instead of stacking.
const notifyQzPrintFailure = (description) => {
  try {
    notification.error({
      key: 'qz-print-failure',
      message: 'Label not printed',
      description,
      top: 65,
    });
  } catch (e) {
    /* no-op */
  }
};

export const printPdfBase64 = async (target, pdfBase64, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => {};
  // Single choke point for the per-user feature toggle: every print helper funnels through here,
  // so a disabled account always gets today's fallback behavior (open/download the PDF).
  if (!isQzFeatureEnabled()) {
    fallback();
    return false;
  }
  // No data to print — a PDF-generation/fetch problem, not a QZ one; keep the fallback.
  if (!pdfBase64) {
    fallback();
    return false;
  }
  const printerName = opts.printer || getSavedPrinter(target);
  // The user opted in to silent printing but this station has no printer chosen for the slot:
  // tell them instead of quietly opening a PDF, and point at where to fix it.
  if (!printerName) {
    notifyQzPrintFailure(
      'QZ Tray printing is enabled but no printer is selected on this computer. Choose your printers under Account → Label Printers.'
    );
    return false;
  }
  try {
    if (!isConnected()) await connect({ retries: 1, delay: 1 });
    const config = qz.configs.create(printerName, {
      size: opts.size || DEFAULT_SIZE[target] || DEFAULT_SIZE.label,
      units: opts.units || 'in',
      orientation: opts.orientation || 'portrait',
      density: opts.density || 203,
      colorType: opts.colorType || 'blackwhite',
      scaleContent: opts.scaleContent !== false,
      rasterize: opts.rasterize === true,
      copies: opts.copies || 1,
    });
    await qz.print(config, [{ type: 'pixel', format: 'pdf', flavor: 'base64', data: pdfBase64 }]);
    return true;
  } catch (e) {
    // Feature on + printer selected + print failed -> tell the operator, don't mask it with a PDF.
    notifyQzPrintFailure(
      'QZ Tray printing is enabled but the printer is not connected. Make sure QZ Tray is running on this computer and the printer is online, then try again.'
    );
    return false;
  }
};

// Convenience for the jsPDF generators: hand it the jsPDF `doc` and it extracts base64 itself.
// jsPDF 1.x: output('datauristring') -> "data:application/pdf;base64,XXXX" -> take the base64 tail.
export const printJsPdf = (target, doc, opts = {}) => {
  let base64;
  try {
    base64 = doc.output('datauristring').split(',')[1];
  } catch (e) {
    if (typeof opts.fallback === 'function') opts.fallback();
    return Promise.resolve(false);
  }
  return printPdfBase64(target, base64, opts);
};

// Convenience for server/S3 PDFs (FBA in/out, carrier, pallet, BoL): fetch -> base64 -> print.
// Fallback defaults to opening the URL in a new tab (today's behavior for those paths).
export const printPdfUrl = async (target, url, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => window.open(url, '_blank');
  try {
    const res = await fetch(url, opts.fetchInit);
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);
    return printPdfBase64(target, base64, { ...opts, fallback });
  } catch (e) {
    fallback();
    return false;
  }
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

// Convenience for already-fetched PDFs held as a Blob (e.g. FBA in/out labels): Blob -> base64 -> print.
// Fallback (download / open) is supplied by the caller and runs on any failure.
export const printPdfBlob = async (target, blob, opts = {}) => {
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => {};
  try {
    const base64 = await blobToBase64(blob);
    return printPdfBase64(target, base64, { ...opts, fallback });
  } catch (e) {
    fallback();
    return false;
  }
};

export default qz;
